import Cookies from 'js-cookie';

export interface UTMParameters {
	utm_source?: string;
	utm_medium?: string;
	utm_campaign?: string;
	utm_content?: string;
	utm_term?: string;
	utm_id?: string;
}

export interface UTMAttributionData {
	first_touch: UTMParameters;
	last_touch: UTMParameters;
}

const UTM_COOKIE_NAME = 'mu_utm_attribution';

export const VALID_UTM_KEYS: Array<keyof UTMParameters> = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_content',
	'utm_term',
	'utm_id',
];

const sanitizeUTMParameters = (obj: unknown): UTMParameters => {
	if (typeof obj !== 'object' || obj === null) {
		return {};
	}
	const record = obj as Record<string, unknown>;
	const result: UTMParameters = {};
	VALID_UTM_KEYS.forEach((key) => {
		if (typeof record[key] === 'string') {
			result[key] = record[key] as string;
		}
	});
	return result;
};

const hasAttributionStructure = (obj: unknown): boolean => {
	if (typeof obj !== 'object' || obj === null) {
		return false;
	}
	const data = obj as Record<string, unknown>;
	return (
		('first_touch' in data && typeof data.first_touch === 'object' && data.first_touch !== null) ||
		('last_touch' in data && typeof data.last_touch === 'object' && data.last_touch !== null)
	);
};

export const getStoredUTMAttribution = (): UTMAttributionData => {
	const emptyAttribution: UTMAttributionData = {
		first_touch: {},
		last_touch: {},
	};

	try {
		const cookieValue = Cookies.get(UTM_COOKIE_NAME);
		if (!cookieValue) {
			return emptyAttribution;
		}

		const parsed = JSON.parse(cookieValue);
		if (!hasAttributionStructure(parsed)) {
			return emptyAttribution;
		}

		return {
			first_touch: sanitizeUTMParameters(parsed.first_touch),
			last_touch: sanitizeUTMParameters(parsed.last_touch),
		};
	} catch {
		return emptyAttribution;
	}
};

export const getFirstTouchUTM = (): UTMParameters => {
	return getStoredUTMAttribution().first_touch;
};

export const getLastTouchUTM = (): UTMParameters => {
	return getStoredUTMAttribution().last_touch;
};

export const hasUTMParameters = (utm: UTMParameters): boolean => {
	return Object.values(utm).some((value) => value !== undefined && value !== '');
};
